import React, { useState } from "react"
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, CircularProgress, Avatar } from "@mui/material"
import { Formik } from "formik"
import i18n from "@/i18n"
import { useEditUserValidationSchema } from "@/forms/edit_user"
import UserFormFields from "./user_form_fields"

interface EditUserDialogProps {
  open: boolean
  onClose: () => void
  onSubmit: (values: any) => void
  user: any
  updating: boolean
}

const EditUserDialog: React.FC<EditUserDialogProps> = ({ open, onClose, onSubmit, user, updating }) => {
  const validationSchema = useEditUserValidationSchema()
  const [avatarFile, setAvatarFile] = useState<File | null>(null)
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null)

  const handleAvatarChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      setAvatarFile(file)
      const reader = new FileReader()
      reader.onloadend = () => {
        setAvatarPreview(reader.result as string)
      }
      reader.readAsDataURL(file)
    }
  }

  const handleRemoveAvatar = () => {
    setAvatarFile(null)
    setAvatarPreview(null)
  }

  const handleFormSubmit = (values: any) => {
    onSubmit({ ...values, avatarFile })
  }

  const handleDialogClose = () => {
    setAvatarFile(null)
    setAvatarPreview(null)
    onClose()
  }

  if (!user) return null

  return (
    <Dialog open={open} onClose={handleDialogClose} maxWidth="md" fullWidth data-testid="settings-users-edit-dialog">
      <DialogTitle data-testid="settings-users-edit-dialog-title">
        {i18n.t("app.settings.users.list.edit_user")}
      </DialogTitle>
      <Formik
        initialValues={{
          firstName: user.firstName || "",
          lastName: user.lastName || "",
          email: user.email || "",
          phone: user.info?.phone || "",
          position: user.info?.position || "",
          country: user.info?.country || "",
          address: user.info?.address || "",
          city: user.info?.city || "",
          postcode: user.info?.postcode || "",
          roles:
            user.roles?.map((r) => ({
              venueId: r.venue?.id,
              role: r.role,
            })) || [],
        }}
        validationSchema={validationSchema}
        enableReinitialize
        onSubmit={handleFormSubmit}
      >
        {(formik) => (
          <form onSubmit={formik.handleSubmit}>
            <DialogContent>
              <div className="flex flex-col gap-4">
                <div className="flex items-center gap-6 mb-4">
                  <Avatar
                    data-testid="settings-users-edit-dialog-avatar"
                    src={avatarPreview || user.avatarUrl || undefined}
                    sx={{ width: 80, height: 80 }}
                  >
                    {user.firstName?.[0]}
                    {user.lastName?.[0]}
                  </Avatar>
                  <div className="flex flex-col justify-center gap-2">
                    <input
                      accept="image/*"
                      style={{ display: "none" }}
                      id="avatar-upload-edit"
                      type="file"
                      onChange={handleAvatarChange}
                      data-testid="settings-users-edit-dialog-avatar-input"
                    />
                    <div className="flex gap-2">
                      <label htmlFor="avatar-upload-edit">
                        <Button
                          variant="outlined"
                          size="small"
                          component="span"
                          data-testid="settings-users-edit-dialog-btn-change-avatar"
                        >
                          {i18n.t("app.settings.profile.overview.change_avatar")}
                        </Button>
                      </label>
                      <Button
                        variant="outlined"
                        size="small"
                        color="error"
                        onClick={handleRemoveAvatar}
                        disabled={!user.avatar && !avatarFile}
                        data-testid="settings-users-edit-dialog-btn-remove-avatar"
                      >
                        {i18n.t("app.settings.profile.overview.remove_avatar")}
                      </Button>
                    </div>
                    <p className="text-sm text-gray-500 m-0">{i18n.t("app.settings.profile.overview.avatar_help")}</p>
                  </div>
                </div>

                <UserFormFields formik={formik} />
              </div>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleDialogClose} color="primary" data-testid="settings-users-edit-dialog-btn-cancel">
                {i18n.t("app.actions.cancel")}
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={updating}
                data-testid="settings-users-edit-dialog-btn-submit"
              >
                {updating ? <CircularProgress size={24} /> : i18n.t("app.actions.save")}
              </Button>
            </DialogActions>
          </form>
        )}
      </Formik>
    </Dialog>
  )
}

export default EditUserDialog
